import { useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListenStats({ secret }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState(null);
  const [sortMode, setSortMode] = useState("journey"); // "journey" (by chapter) | "top" (by listens)

  const load = () => {
    setLoading(true);
    fetch(`${API}/admin/${secret}/listen-stats`)
      .then((r) => {
        if (r.status === 404) throw new Error("Invalid admin link");
        return r.json();
      })
      .then((d) => {
        setData(d);
        setErr("");
        const first = (d.by_language || [])[0];
        if (first) setActiveLang(first.code);
      })
      .catch((e) => setErr(e.message || "Failed"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [secret]);

  if (loading) return <div className="admin-muted">Loading analytics…</div>;
  if (err) return <div className="admin-err" data-testid="listen-stats-err">{err}</div>;
  if (!data) return null;

  const totals = (data.by_language || []).reduce(
    (acc, r) => ({
      listens: acc.listens + r.total_listens,
      uniques: acc.uniques + r.unique_visitors,
    }),
    { listens: 0, uniques: 0 }
  );

  // Best completion rate across active languages (most representative single
  // headline). Treat zero-listener languages as N/A.
  const completionLeader = (data.by_language || [])
    .filter((l) => l.unique_visitors > 0)
    .reduce(
      (best, l) => (l.completion_pct > (best?.completion_pct ?? -1) ? l : best),
      null
    );

  const chaptersForLang = (data.by_chapter || {})[activeLang] || [];
  const maxListens = chaptersForLang.reduce((m, c) => Math.max(m, c.listens), 0) || 1;
  const sortedChapters =
    sortMode === "top"
      ? [...chaptersForLang].sort((a, b) => b.listens - a.listens)
      : [...chaptersForLang].sort((a, b) => a.chapter - b.chapter);

  return (
    <section className="listen-stats" data-testid="listen-stats">
      <div className="listen-toolbar">
        <h2 className="listen-title">Listen analytics</h2>
        <div className="listen-toolbar-actions">
          <a
            href={`${API}/admin/${secret}/spotify-bundle.zip`}
            className="admin-btn admin-btn-primary"
            data-testid="download-spotify-bundle"
            title="Build a drag-drop ready ZIP with tagged MP3s, cover art, episodes.csv and a README for Spotify for Creators / Storylo upload."
          >
            ↓ Spotify / Storylo bundle
          </a>
          <button onClick={load} className="admin-btn" data-testid="listen-refresh">
            Refresh
          </button>
        </div>
      </div>

      <div className="listen-summary">
        <div className="listen-stat">
          <span className="listen-stat-num" data-testid="listen-total-listens">
            {totals.listens.toLocaleString()}
          </span>
          <span className="listen-stat-label">total listens</span>
        </div>
        <div className="listen-stat">
          <span className="listen-stat-num" data-testid="listen-total-uniques">
            {totals.uniques.toLocaleString()}
          </span>
          <span className="listen-stat-label">unique listeners</span>
        </div>
        <div className="listen-stat">
          <span className="listen-stat-num">{(data.by_language || []).length}</span>
          <span className="listen-stat-label">active languages</span>
        </div>
        <div className="listen-stat" data-testid="listen-completion">
          <span className="listen-stat-num">
            {completionLeader ? `${completionLeader.completion_pct}%` : "—"}
          </span>
          <span className="listen-stat-label">
            finish rate{completionLeader ? ` · ${completionLeader.name}` : ""}
          </span>
        </div>
      </div>

      <div className="listen-langs" data-testid="listen-langs">
        <table>
          <thead>
            <tr>
              <th>Language</th>
              <th>Listens</th>
              <th>Unique</th>
              <th>Finish %</th>
              <th>Last</th>
            </tr>
          </thead>
          <tbody>
            {(data.by_language || []).map((r) => (
              <tr
                key={r.code}
                className={r.code === activeLang ? "listen-lang-active" : ""}
                onClick={() => setActiveLang(r.code)}
                data-testid={`listen-lang-${r.code}`}
              >
                <td>
                  <strong>{r.name}</strong> <span className="listen-mute">({r.name_en})</span>
                </td>
                <td>{r.total_listens.toLocaleString()}</td>
                <td>{r.unique_visitors.toLocaleString()}</td>
                <td data-testid={`listen-finish-${r.code}`}>
                  {r.completion_pct ? `${r.completion_pct}%` : "—"}
                </td>
                <td className="listen-mute">
                  {r.last_listened ? new Date(r.last_listened).toLocaleString() : "—"}
                </td>
              </tr>
            ))}
            {(data.by_language || []).length === 0 && (
              <tr><td colSpan={5} className="listen-mute">No listens recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {activeLang && chaptersForLang.length > 0 && (
        <div className="listen-chapters" data-testid={`listen-chapters-${activeLang}`}>
          <div className="listen-chapter-head">
            <h3 className="listen-sub">
              Chapter heatmap —{" "}
              {(data.by_language || []).find((l) => l.code === activeLang)?.name}
            </h3>
            <div className="listen-sort" role="tablist" aria-label="Sort chapters">
              <button
                type="button"
                onClick={() => setSortMode("journey")}
                className={"listen-sort-btn" + (sortMode === "journey" ? " active" : "")}
                data-testid="listen-sort-journey"
              >
                Listener journey
              </button>
              <button
                type="button"
                onClick={() => setSortMode("top")}
                className={"listen-sort-btn" + (sortMode === "top" ? " active" : "")}
                data-testid="listen-sort-top"
              >
                Most listened
              </button>
            </div>
          </div>
          <ol className="listen-chapter-list">
            {sortedChapters.map((c) => (
              <li key={c.chapter} className="listen-chapter">
                <span className="listen-chapter-idx">
                  {String(c.chapter).padStart(2, "0")}
                </span>
                <span className="listen-chapter-title" title={c.title}>{c.title}</span>
                <span className="listen-chapter-bar">
                  <span
                    className="listen-chapter-fill"
                    style={{ width: `${(c.listens / maxListens) * 100}%` }}
                  />
                </span>
                <span className="listen-chapter-count">
                  {c.listens} <span className="listen-mute">({c.uniques})</span>
                  {typeof c.reach_pct === "number" && (
                    <span className="listen-chapter-reach" data-testid={`reach-${activeLang}-${c.chapter}`}>
                      {c.reach_pct}%
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ol>
          <p className="listen-foot-hint">
            <strong>Listener journey</strong> shows drop-off in chapter order — the
            ideal shape is a flat line near 100%. <strong>Reach %</strong> = share of
            this language&apos;s unique listeners who reached this chapter.
          </p>
        </div>
      )}

      {(data.daily || []).length > 1 && (
        <div className="listen-daily" data-testid="listen-daily">
          <h3 className="listen-sub">Daily trend (last 30 days)</h3>
          <div className="listen-daily-bars">
            {(() => {
              const maxDay = Math.max(...data.daily.map((d) => d.listens), 1);
              return data.daily.map((d) => (
                <div className="listen-daily-col" key={d.day} title={`${d.day}: ${d.listens}`}>
                  <div
                    className="listen-daily-bar"
                    style={{ height: `${(d.listens / maxDay) * 100}%` }}
                  />
                  <div className="listen-daily-label">{d.day.slice(5)}</div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </section>
  );
}
